/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';

import { EventAnnotationTooltip } from './Tooltips';
import { Event, Alert } from './constants';
import { AlertIcon } from './icons/AlertIcon';
import { CustomEventIcon } from './icons/CustomEventIcon';
import { PlotUtilities } from './renderers/types';
import { useVizTheme, VizTheme } from './theme';
import { AlertAnnotationSeverity, AnnotationType } from './types';
import { useScaleFactor } from './utilities/useScaleFactor';

export type EventAnnotation = {
    // unscaled time
    time: number;
    key?: string;
    type?: AnnotationType;
    severity?: AlertAnnotationSeverity;
    isCleared?: boolean;
};

export type EventAnnotationIconProps = {
    theme: VizTheme;
    dimensions: { width: number; height: number };
    isCleared?: boolean;
};

export type EventAnnotationRendererProps = {
    event: EventAnnotation;
} & EventAnnotationIconProps;

export type EventAnnotationWResolution = EventAnnotation & {
    xResolution?: number;
};
export type EventAnnotationConfig = {
    renderer?: React.FC<EventAnnotationRendererProps>;
    tooltip?: EventAnnotationTooltip;
    onEventAnnotationHover?: (timestamp: number | undefined) => void;
    onEventAnnotationClick?: (
        event: EventAnnotationWResolution,
        mouseEvent?: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => void;
};

// Dimensions of rendered annotation
export const EVENT_ANNOTATION_DIMENSIONS = {
    width: 12,
    height: 12,
};

type Props = {
    plotUtilities?: PlotUtilities;
    events?: EventAnnotation[];
    xResolution?: number;
    eventAnnotationConfig?: EventAnnotationConfig;
    onEventAnnotationHover: (event?: EventAnnotation) => void;
    onEventAnnotationClick?: (
        event: EventAnnotationWResolution,
        mouseEvent?: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => void;
};

export const EventAnnotationContainer: React.FC<Props> = props => {
    const {
        plotUtilities,
        events,
        eventAnnotationConfig,
        onEventAnnotationHover,
        onEventAnnotationClick,
        xResolution,
    } = props;
    const theme = useVizTheme();
    const scaleFactor = useScaleFactor();

    // Force re-render when the x-scale changes (e.g. via client-side zoom/pan in RangeSelection)
    // so the visibleEvents filter below stays in sync with the current visible range.
    const [, forceUpdate] = React.useReducer((count: number) => count + 1, 0);
    React.useEffect(() => {
        if (!plotUtilities?.subscribeToScale) {
            return undefined;
        }
        return plotUtilities.subscribeToScale('x', forceUpdate);
    }, [plotUtilities]);

    // SCP-80914: observe width and height of .u-over so annotations re-render (and re-call
    // valToPos) after uPlot's second-phase layout; height-only changes were insufficient when
    // only plot width shifted. Use getBoundingClientRect (not entry.contentRect) to match the
    // coordinate space of plotUtilities.dimensions.over; contentRect reports the pre-transform
    // box and would drift by `scaleFactor` under a zoomed dashboard.
    const snapshotOverHeight = plotUtilities?.dimensions.over.height;
    const [liveOverRect, setLiveOverRect] = useState<{ width: number; height: number } | undefined>(
        undefined
    );
    const observerRef = useRef<ResizeObserver | null>(null);

    useEffect(() => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        const el = plotUtilities?.el?.querySelector<HTMLElement>('.u-over');
        if (!el) {
            // Fall back to the snapshotted height until .u-over is available.
            if (snapshotOverHeight !== undefined) {
                setLiveOverRect(prev =>
                    prev?.height === snapshotOverHeight ? prev : { width: 0, height: snapshotOverHeight }
                );
            }
            return undefined;
        }

        const measure = (): void => {
            const r = el.getBoundingClientRect();
            setLiveOverRect(prev =>
                prev?.width === r.width && prev?.height === r.height
                    ? prev
                    : { width: r.width, height: r.height }
            );
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        observerRef.current = observer;

        return () => observer.disconnect();
    }, [plotUtilities?.el, snapshotOverHeight]);

    if (!events || !plotUtilities) {
        return null;
    }

    const overHeight = liveOverRect?.height ?? plotUtilities.dimensions.over.height;

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const annotationRenderer = getEventAnnotationRenderer(eventAnnotationConfig);

    // Get current visible scale range to filter out annotations outside bounds
    const scaleRange = plotUtilities.getScaleRange('x');

    // Filter events to only show those within visible range
    const visibleEvents = scaleRange
        ? events.filter(event => event.time >= scaleRange.min && event.time <= scaleRange.max)
        : events;

    return (
        <div className="olly-viz-event-annotation-wrap" style={{ position: 'absolute' }}>
            {visibleEvents.map(event => {
                const scaledWidth = EVENT_ANNOTATION_DIMENSIONS.width * scaleFactor;
                const scaledHeight = EVENT_ANNOTATION_DIMENSIONS.height * scaleFactor;
                // Center the icon on the tick using the rendered (scaled) width.
                const x = plotUtilities.valToPos(event.time, 'x') - scaledWidth / 2;
                const y = overHeight / scaleFactor;
                const annotationNode = annotationRenderer({
                    event,
                    theme,
                    dimensions: {
                        width: scaledWidth,
                        height: scaledHeight,
                    },
                });

                return (
                    <div
                        key={`${event.key || ''}${event.time}`}
                        className="olly-viz-event-annotation"
                        data-test="viz-event-annotation"
                        style={{
                            position: 'absolute',
                            left: `${x}px`,
                            top: `${y}px`,
                            width: `${scaledWidth}px`,
                            height: `${scaledHeight}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'auto',
                        }}
                        onMouseEnter={(): void => onEventAnnotationHover(event)}
                        onMouseLeave={(): void => onEventAnnotationHover(undefined)}
                        onClick={(mouseEvent): void => {
                            if (onEventAnnotationClick) {
                                onEventAnnotationClick({ ...event, xResolution }, mouseEvent);
                            }
                        }}
                    >
                        {annotationNode}
                    </div>
                );
            })}
        </div>
    );
};

export const getEventAnnotationRenderer = (
    eventAnnotationConfig?: EventAnnotationConfig
): React.FC<EventAnnotationRendererProps> => {
    const defaultAnnotationRenderer: React.FC<EventAnnotationRendererProps> = ({
        event,
        theme,
        dimensions,
    }) => {
        switch (event.type) {
            case Alert:
                return (
                    <AlertIcon
                        theme={theme}
                        dimensions={dimensions}
                        severity={event.severity}
                        isCleared={event.isCleared}
                    />
                );
            case Event:
            default:
                return <CustomEventIcon theme={theme} dimensions={dimensions} isCleared={event.isCleared} />;
        }
    };

    return eventAnnotationConfig?.renderer || defaultAnnotationRenderer;
};
